import { BaseEmailProvider } from '../BaseEmailProvider';
import type { EmailData, RegistrationNotificationData, EmailResponse } from '../types';

/**
 * API Email Provider - Uses backend API endpoint for email sending
 */
export class APIEmailProvider extends BaseEmailProvider {
  protected providerName = 'API';
  private apiBaseUrl: string;

  constructor() {
    super();
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
  }

  /**
   * Validate API configuration
   */
  validateConfiguration(): boolean {
    return !!this.apiBaseUrl;
  }

  /**
   * Send contact email via API
   */
  async sendEmail(data: EmailData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'contact');

      if (!this.validateConfiguration()) {
        throw new Error('API base URL is not configured');
      }

      const emailContent = this.generateContactEmailContent(data);
      
      const response = await fetch(`${this.apiBaseUrl}/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          to: [emailContent.toEmail],
          subject: emailContent.subject,
          content: emailContent.htmlContent,
          contentType: 'HTML'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      this.logEmailSuccess(result.messageId || 'api-success', emailContent.toEmail);

      return {
        success: true,
        message: 'Email successfully sent via API',
        messageId: result.messageId
      };

    } catch (error: any) {
      this.logEmailError(error, data.to);
      
      return {
        success: false,
        message: `API error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send registration notification via API
   */
  async sendRegistrationNotification(data: RegistrationNotificationData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'registration');

      if (!this.validateConfiguration()) {
        throw new Error('API base URL is not configured');
      }

      const emailContent = this.generateRegistrationEmailContent(data);
      
      const response = await fetch(`${this.apiBaseUrl}/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          to: [emailContent.toEmail],
          subject: emailContent.subject,
          content: emailContent.htmlContent,
          contentType: 'HTML'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      this.logEmailSuccess(result.messageId || 'api-success', emailContent.toEmail);

      return {
        success: true,
        message: 'Registration notification sent successfully via API',
        messageId: result.messageId
      };

    } catch (error: any) {
      this.logEmailError(error, this.getToEmail());
      
      return {
        success: false,
        message: `API error: ${error.message || 'Unknown error'}`
      };
    }
  }
}
